import React, { useState, useEffect, useCallback } from 'react';
import { 
    getMkpuBalance, 
    getMkpuPricing, 
    purchaseMkpu, 
    setMkpuThreshold 
} from '../services/api';

const MkpuPage = () => {
    const [balance, setBalance] = useState(0);
    const [expiryDate, setExpiryDate] = useState(null); // 【新增】到期日的 state
    const [pricing, setPricing] = useState(null);
    const [threshold, setThreshold] = useState('');
    const [purchaseQuantity, setPurchaseQuantity] = useState(30);
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const balanceRes = await getMkpuBalance();
            setBalance(balanceRes.data.data?.mKPU || 0);
            setThreshold(balanceRes.data.data?.threshold || '');
            // 【新增】從 API 回應中設定到期日
            setExpiryDate(balanceRes.data.data?.endDate || null); 

            const pricingRes = await getMkpuPricing();
            setPricing(pricingRes.data.data?.[0] || null);
        } catch (err) {
            setError('無法獲取 MKPU 資料，請稍後再試。');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handlePurchase = async (e) => {
        e.preventDefault();
        
        if (purchaseQuantity < 30 || purchaseQuantity % 10 !== 0) {
            alert('購買數量必須大於等於 30，且為 10 的倍數。');
            return;
        }

        try {
            const response = await purchaseMkpu({
                unit: pricing.unit,
                quantity: purchaseQuantity
            });
            
            if (response.data.data?.action === 'pending payment authorization') {
                 alert('購買需要付款驗證，將為您開啟驗證分頁。完成後請回到本頁面重新整理。');
                 if(response.data.data.threeDUrl) {
                    window.open(response.data.data.threeDUrl, '_blank');
                 }
            } else {
                alert('購買成功！');
                fetchData();
            }
        } catch (err) {
            alert(`購買失敗：${err.response?.data?.message || '發生未知錯誤'}`);
        }
    };

    const handleSetThreshold = async (e) => {
        e.preventDefault();
        const numericThreshold = parseInt(threshold, 10);
        if (isNaN(numericThreshold) || numericThreshold < 0) {
            alert('請輸入有效的非負數字。');
            return;
        }

        try {
            await setMkpuThreshold(numericThreshold);
            alert('低餘額提醒已成功設定！');
            fetchData();
        } catch (err) {
            alert(`設定失敗：${err.response?.data?.message || '發生未知錯誤'}`);
        }
    };
    
    // 格式化日期，如果日期無效則回傳提示文字
    const formatDate = (dateString) => {
        if (!dateString) return '無';
        const date = new Date(dateString);
        if (isNaN(date)) return '無';
        return date.toLocaleDateString();
    };

    if (loading) return <div className="page-container">正在載入 MKPU 資料...</div>;
    if (error) return <p className="error-message">{error}</p>;

    return (
        <div className="page-container">
            <div className="mkpu-header">
                <div className="balance-display">
                    <h2>目前餘額</h2>
                    <p className="balance-amount">{balance.toLocaleString()} <span>MKPU</span></p>
                    {/* 【新增】顯示到期日 */}
                    <p className="expiry-date">到期日: {formatDate(expiryDate)}</p>
                </div>
                <button onClick={fetchData} className="refresh-button">重新整理</button>
            </div>

            <div className="mkpu-section">
                <h3>購買點數</h3>
                {pricing ? (
                    <form onSubmit={handlePurchase} className="purchase-form">
                        <div className="unit-price">
                            單位價格: <span>TWD ${pricing.price} / {pricing.unit}</span>
                        </div>
                        <div className="purchase-controls">
                            <label htmlFor="quantity">購買數量 (10的倍數, 最低30):</label>
                            <input
                                type="number"
                                id="quantity"
                                value={purchaseQuantity}
                                onChange={(e) => setPurchaseQuantity(parseInt(e.target.value, 10))}
                                min="30"
                                step="10"
                                className="purchase-input"
                            />
                            <button type="submit" className="purchase-button">確認購買</button>
                        </div>
                    </form>
                ) : (
                    <p>目前無可用購買方案。</p>
                )}
            </div>

            <div className="mkpu-section">
                <h3>設定低餘額提醒</h3>
                <form onSubmit={handleSetThreshold} className="threshold-form">
                    <input 
                        type="number" 
                        value={threshold}
                        onChange={(e) => setThreshold(e.target.value)}
                        placeholder="輸入點數"
                        className="threshold-input"
                    />
                    <button type="submit" className="action-button default">儲存設定</button>
                </form>
            </div>
        </div>
    );
};

export default MkpuPage;